<template>
  <div>
    <!-- Performance section -->
    <v-card outlined class="pa-6 mb-4">
      <h3 class="black--text mb-1">{{ $t("reports.aiCredit.perfTitle") }}</h3>
      <p class="grey--text mb-4" style="max-width: 1020px">
        {{ $t("reports.aiCredit.perfHelp") }}
      </p>

      <v-row class="mb-1" dense>
        <v-col cols="12" sm="4" md="3">
          <v-select
            v-model="perf.organization"
            :items="cooperativeOptions"
            :label="$t('reports.aiCredit.filterCooperative')"
            :placeholder="$t('reports.aiCredit.makeSelection')"
            outlined
            dense
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="4" md="3">
          <v-select
            v-model="perf.reportType"
            :items="reportTypeOptions"
            :label="$t('reports.aiCredit.filterReportType')"
            :placeholder="$t('reports.aiCredit.makeSelection')"
            outlined
            dense
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="4" md="3">
          <date-range-field
            v-model="perf.dateRange"
            :label="$t('reports.aiCredit.filterDateRange')"
            :placeholder="$t('reports.aiCredit.selectDate')"
          />
        </v-col>
      </v-row>

      <v-progress-linear
        v-if="perfLoading"
        indeterminate
        color="primary"
        class="mb-1"
      />
      <div v-else style="height: 4px" class="mb-1"></div>

      <v-row>
        <!-- Satisfaction gauge -->
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4" style="height: 100%">
            <div class="black--text font-weight-bold mb-2">
              {{ $t("reports.aiCredit.satisfactionTitle") }}
            </div>
            <apexchart
              type="radialBar"
              height="220"
              :options="gaugeOptions"
              :series="gaugeSeries"
            />
            <div class="d-flex align-center justify-center" style="gap: 12px">
              <span class="text-h3 font-weight-bold black--text"
                >{{ perfSatisfaction }}%</span
              >
              <span class="grey--text text--darken-1">{{
                $t("reports.aiCredit.agreementScore")
              }}</span>
            </div>
          </v-card>
        </v-col>

        <!-- Trend -->
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4" style="height: 100%">
            <div class="black--text font-weight-bold mb-2">
              {{ $t("reports.aiCredit.trendTitle") }}
            </div>
            <apexchart
              type="area"
              height="260"
              :options="trendOptions"
              :series="trendSeries"
            />
          </v-card>
        </v-col>

        <!-- Top 3 disputed -->
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4" style="height: 100%">
            <div class="black--text font-weight-bold mb-3">
              {{ $t("reports.aiCredit.topDisputedTitle") }}
            </div>
            <div class="d-flex grey--text text-caption px-1 mb-2">
              <span style="width: 48px">{{
                $t("reports.aiCredit.colRank")
              }}</span>
              <span>{{ $t("reports.aiCredit.colReportName") }}</span>
            </div>
            <div v-if="!topDisputed.length" class="grey--text">
              {{ $t("reports.aiCredit.noData") }}
            </div>
            <div
              v-for="(row, i) in topDisputed"
              :key="row.reportType"
              class="d-flex align-center py-2"
            >
              <span
                class="black--text font-weight-medium"
                style="width: 48px"
                >{{ i + 1 }}</span
              >
              <div style="flex: 1 1 auto">
                <div class="black--text font-weight-medium mb-1">
                  {{ reportLabel(row.reportType) }}
                </div>
                <div class="d-flex align-center">
                  <div class="dispute-bar mr-2">
                    <div
                      class="dispute-bar-fill"
                      :style="{ width: barWidth(row.count) + '%' }"
                    ></div>
                  </div>
                  <span
                    class="grey--text text-caption"
                    style="white-space: nowrap"
                  >
                    {{ row.count }} {{ $t("reports.aiCredit.disagrees") }}
                  </span>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <!-- Feedback list -->
    <v-card outlined class="pa-6">
      <h3 class="black--text mb-3">
        {{ $t("reports.aiCredit.feedbackListTitle2") }}
      </h3>

      <v-row class="mb-1" dense>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="list.search"
            :placeholder="$t('reports.aiCredit.search')"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="6" md="2">
          <v-select
            v-model="list.reportType"
            :items="reportTypeOptions"
            :label="$t('reports.aiCredit.filterReportType')"
            :placeholder="$t('reports.aiCredit.makeSelection')"
            outlined
            dense
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="6" md="2">
          <v-select
            v-model="list.organization"
            :items="cooperativeOptions"
            :label="$t('reports.aiCredit.filterCooperative')"
            :placeholder="$t('reports.aiCredit.makeSelection')"
            outlined
            dense
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="6" md="2">
          <v-select
            v-model="list.feedbackValue"
            :items="feedbackValueOptions"
            :label="$t('reports.aiCredit.filterFeedbackValue')"
            :placeholder="$t('reports.aiCredit.makeSelection')"
            outlined
            dense
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="6" md="3" class="d-flex align-center">
          <date-range-field
            v-model="list.dateRange"
            :label="$t('reports.aiCredit.filterDateRange')"
            :placeholder="$t('reports.aiCredit.selectDate')"
            class="flex-grow-1"
          />
          <div class="d-flex align-center ml-3">
            <span class="grey--text mr-2" style="white-space: nowrap">{{
              rangeLabel
            }}</span>
            <v-btn
              icon
              outlined
              color="primary"
              class="mr-1"
              :disabled="page <= 1"
              @click="prevPage"
              ><v-icon>mdi-chevron-left</v-icon></v-btn
            >
            <v-btn
              icon
              outlined
              color="primary"
              :disabled="!hasNextPage"
              @click="nextPage"
              ><v-icon>mdi-chevron-right</v-icon></v-btn
            >
          </div>
        </v-col>
      </v-row>

      <!-- Metric cards -->
      <v-row class="mb-2" dense>
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4 d-flex align-center">
            <v-avatar color="grey darken-2" size="48" class="mr-4"
              ><v-icon dark>mdi-star-four-points</v-icon></v-avatar
            >
            <div>
              <div class="grey--text text--darken-1 font-weight-medium">
                {{ $t("reports.aiCredit.mTotalReports") }}
              </div>
              <div class="text-h5 font-weight-bold black--text">
                {{ formatNum(metrics.total) }}
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4 d-flex align-center metric-agree">
            <v-avatar color="green" size="48" class="mr-4"
              ><v-icon dark>mdi-thumb-up</v-icon></v-avatar
            >
            <div>
              <div class="grey--text text--darken-1 font-weight-medium">
                {{ $t("reports.aiCredit.mFeedbackAgree") }}
              </div>
              <div class="text-h5 font-weight-bold black--text">
                {{ formatNum(metrics.agree) }}
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4 d-flex align-center metric-disagree">
            <v-avatar color="red" size="48" class="mr-4"
              ><v-icon dark>mdi-thumb-down</v-icon></v-avatar
            >
            <div>
              <div class="grey--text text--darken-1 font-weight-medium">
                {{ $t("reports.aiCredit.mFeedbackDisagree") }}
              </div>
              <div class="text-h5 font-weight-bold black--text">
                {{ formatNum(metrics.disagree) }}
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Feedback table -->
      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        :server-items-length="total"
        hide-default-footer
        class="elevation-0 feedback-table"
      >
        <template v-slot:[`item.timestamp`]="{ item }">
          <div class="black--text">{{ fmtDateTime(item.date) }}</div>
          <div class="black--text font-weight-medium">
            {{ reportLabel(item.reportType) }}
          </div>
          <div class="grey--text text-caption">{{ reportCode(item) }}</div>
        </template>
        <template v-slot:[`item.user`]="{ item }">
          <div class="black--text">{{ item.user || "-" }}</div>
          <div class="grey--text text-caption">{{ item.userRole || "" }}</div>
          <div class="black--text font-weight-medium">
            {{ item.cooperative || "" }}
          </div>
        </template>
        <template v-slot:[`item.agree`]="{ item }">
          <div class="d-flex align-center">
            <v-avatar
              :color="item.agree ? 'green' : 'red'"
              size="26"
              class="mr-2"
            >
              <v-icon dark x-small>{{
                item.agree ? "mdi-thumb-up" : "mdi-thumb-down"
              }}</v-icon>
            </v-avatar>
            <span
              :class="item.agree ? 'green--text text--darken-1' : 'red--text'"
            >
              {{
                item.agree
                  ? $t("reports.aiCredit.agree")
                  : $t("reports.aiCredit.disagree")
              }}
            </span>
          </div>
        </template>
        <template v-slot:[`item.comment`]="{ item }">
          <span v-if="item.comment">{{ item.comment }}</span>
          <span v-else class="grey--text">{{ $t("reports.aiCredit.na") }}</span>
        </template>
        <template v-slot:[`item.attachments`]="{ item }">
          <div v-if="item.attachments && item.attachments.length">
            <div
              v-for="(att, i) in item.attachments"
              :key="att.id != null ? att.id : i"
              class="d-flex align-center mb-1 att-link"
              @click="openAttachments(item.attachments, i)"
            >
              <v-avatar
                :color="isImage(att) ? 'green' : 'blue'"
                size="22"
                class="mr-2"
              >
                <v-icon dark x-small>{{
                  isImage(att) ? "mdi-image" : "mdi-play"
                }}</v-icon>
              </v-avatar>
              <span
                class="green--text text--darken-2 text-decoration-underline text-truncate"
                style="max-width: 160px"
              >
                {{ att.fileName || "attachment" }}
              </span>
            </div>
          </div>
          <span v-else class="grey--text">{{ $t("reports.aiCredit.na") }}</span>
        </template>
      </v-data-table>
    </v-card>

    <feedback-attachment-dialog
      :value="attachmentDialog"
      :attachments="selectedAttachments"
      :start-index="selectedStart"
      @input="attachmentDialog = $event"
    />
  </div>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";
import creditFormat from "@/mixins/creditFormat";
import FeedbackAttachmentDialog from "./FeedbackAttachmentDialog.vue";
import DateRangeField from "./DateRangeField.vue";

const REPORT_TYPES = [
  "vegetation_vigor",
  "farm_productivity",
  "anomaly_detection",
  "yield_prediction",
  "land_suitability",
  "disease_detection",
];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default {
  name: "FeedbackDashboard",
  mixins: [creditFormat],
  components: { FeedbackAttachmentDialog, DateRangeField },
  data() {
    return {
      // Performance cards (gauge / trend / top-3) — own independent filters.
      perf: { organization: null, reportType: null, dateRange: [] },
      perfSatisfaction: 0,
      topDisagreed: [],
      trend: [],
      perfLoading: false,

      // Feedback list (metric cards + table) — own independent filters.
      list: {
        organization: null,
        reportType: null,
        feedbackValue: null,
        dateRange: [],
        search: "",
      },
      metrics: { total: 0, agree: 0, disagree: 0 },
      rows: [],
      total: 0,
      page: 1,
      limit: 10,
      loading: false,
      searchTimer: null,

      cooperativeOptions: [],
      attachmentDialog: false,
      selectedAttachments: [],
      selectedStart: 0,
    };
  },
  computed: {
    reportTypeOptions() {
      return REPORT_TYPES.map((rt) => ({
        text: this.reportLabel(rt),
        value: rt,
      }));
    },
    feedbackValueOptions() {
      return [
        { text: this.$t("reports.aiCredit.agree"), value: "agree" },
        { text: this.$t("reports.aiCredit.disagree"), value: "disagree" },
      ];
    },
    headers() {
      const t = (k) => this.$t(`reports.aiCredit.${k}`);
      return [
        { text: t("colTimestampReport"), value: "timestamp", sortable: false },
        { text: t("colUserNameRole"), value: "user", sortable: false },
        { text: t("colFeedbackValue"), value: "agree", sortable: false },
        { text: t("colComments"), value: "comment", sortable: false },
        { text: t("colAttachments"), value: "attachments", sortable: false },
      ];
    },
    topDisputed() {
      return (this.topDisagreed || []).slice(0, 3);
    },
    gaugeSeries() {
      return [Number(this.perfSatisfaction) || 0];
    },
    gaugeOptions() {
      return {
        chart: { type: "radialBar", sparkline: { enabled: true } },
        plotOptions: {
          radialBar: {
            startAngle: -90,
            endAngle: 90,
            hollow: { size: "58%" },
            track: { background: "#eeeeee", strokeWidth: "100%" },
            dataLabels: { show: false },
          },
        },
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "horizontal",
            colorStops: [
              { offset: 0, color: "#e74c3c" },
              { offset: 50, color: "#f1c40f" },
              { offset: 100, color: "#21b573" },
            ],
          },
        },
        stroke: { lineCap: "round" },
      };
    },
    trendSeries() {
      return [
        {
          name: this.$t("reports.aiCredit.agree"),
          data: this.trend.map((t) => t.agree),
        },
        {
          name: this.$t("reports.aiCredit.disagree"),
          data: this.trend.map((t) => t.disagree),
        },
      ];
    },
    // Y-axis ceiling = the highest agree/disagree count across months + padding.
    trendMax() {
      const peak = this.trend.reduce(
        (m, t) => Math.max(m, t.agree || 0, t.disagree || 0),
        0
      );
      if (peak <= 0) return 5;
      return peak + Math.max(1, Math.ceil(peak * 0.15));
    },
    trendOptions() {
      return {
        chart: {
          type: "area",
          toolbar: { show: false },
          fontFamily: "inherit",
        },
        colors: ["#21b573", "#e74c3c"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: {
          type: "gradient",
          gradient: { opacityFrom: 0.4, opacityTo: 0.05 },
        },
        legend: { position: "top", horizontalAlign: "right" },
        xaxis: {
          categories: this.trend.map((t) => this.monthShort(t.month)),
          title: { text: this.$t("reports.aiCredit.timePeriod") },
        },
        yaxis: {
          min: 0,
          max: this.trendMax,
          forceNiceScale: true,
          title: { text: this.$t("reports.aiCredit.reportCount") },
        },
        grid: { borderColor: "#eeeeee" },
      };
    },
    hasNextPage() {
      return this.page * this.limit < this.total;
    },
    rangeLabel() {
      const shown = Math.min(this.page * this.limit, this.total);
      return `${shown} ${this.$t("of")} ${this.total}`;
    },
  },
  watch: {
    "perf.organization"() {
      this.fetchPerformance();
    },
    "perf.reportType"() {
      this.fetchPerformance();
    },
    "perf.dateRange"() {
      this.fetchPerformance();
    },
    "list.organization"() {
      this.reloadList();
    },
    "list.reportType"() {
      this.reloadList();
    },
    "list.feedbackValue"() {
      this.reloadList();
    },
    "list.dateRange"() {
      this.reloadList();
    },
    "list.search"() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.reloadList(), 500);
    },
  },
  created() {
    this.fetchPerformance();
    this.fetchList();
    this.loadCooperatives();
  },
  methods: {
    dateParams(dateRange, params) {
      if (dateRange && dateRange.length) {
        params.from = dateRange[0];
        params.to = dateRange[1] || dateRange[0];
      }
    },
    // Aggregates only (gauge / trend / top-3). Minimal list payload.
    async fetchPerformance() {
      this.perfLoading = true;
      try {
        const params = { page: 1, limit: 1 };
        if (this.perf.organization)
          params.organization = this.perf.organization;
        if (this.perf.reportType) params.reportType = this.perf.reportType;
        this.dateParams(this.perf.dateRange, params);
        const env = await AiCreditService.feedback(params);
        const payload = (env && env.data) || {};
        this.perfSatisfaction =
          (payload.metrics && payload.metrics.satisfactionRate) || 0;
        this.topDisagreed = payload.topDisagreed || [];
        this.trend = payload.trend || [];
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiCredit.errorTitle"),
          text: this.$t("reports.aiCredit.loadFailed"),
          type: "error",
        });
      } finally {
        this.perfLoading = false;
      }
    },
    reloadList() {
      this.page = 1;
      this.fetchList();
    },
    // Metric cards + paginated table.
    async fetchList() {
      this.loading = true;
      try {
        const params = { page: this.page, limit: this.limit };
        if (this.list.organization)
          params.organization = this.list.organization;
        if (this.list.reportType) params.reportType = this.list.reportType;
        if (this.list.feedbackValue) params.agree = this.list.feedbackValue;
        if (this.list.search) params.search = this.list.search;
        this.dateParams(this.list.dateRange, params);
        const env = await AiCreditService.feedback(params);
        const payload = (env && env.data) || {};
        this.metrics = payload.metrics || { total: 0, agree: 0, disagree: 0 };
        this.rows = payload.feedback || [];
        this.total = (payload.pagination && payload.pagination.total) || 0;
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiCredit.errorTitle"),
          text: this.$t("reports.aiCredit.loadFailed"),
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    async loadCooperatives() {
      try {
        const env = await AiCreditService.listCooperatives({
          page: 1,
          limit: 200,
        });
        const list = (env && env.data && env.data.cooperatives) || [];
        this.cooperativeOptions = list.map((c) => ({
          text: c.cooperative,
          value: c.organization,
        }));
      } catch (e) {
        // optional filter
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1;
        this.fetchList();
      }
    },
    nextPage() {
      if (this.hasNextPage) {
        this.page += 1;
        this.fetchList();
      }
    },
    openAttachments(attachments, index) {
      this.selectedAttachments = attachments || [];
      this.selectedStart = index || 0;
      this.attachmentDialog = true;
    },
    barWidth(count) {
      const max = Math.max(...this.topDisputed.map((r) => r.count), 1);
      return Math.max(6, Math.round((count / max) * 100));
    },
    monthShort(ym) {
      if (!ym) return "";
      const m = Number(String(ym).split("-")[1]);
      return MONTHS[m - 1] || ym;
    },
    reportCode(item) {
      return item.reportId != null ? `#${item.reportId}` : "";
    },
    reportLabel(reportType) {
      const key = `reports.aiCredit.rt_${reportType}`;
      const label = this.$t(key);
      return label === key ? reportType : label;
    },
    isImage(att) {
      if (!att) return false;
      if (att.mimeType) return att.mimeType.startsWith("image/");
      return att.fileType === "image";
    },
  },
};
</script>

<style scoped>
.feedback-table >>> thead th {
  background: #1b7a43;
  color: #fff !important;
  font-weight: 600;
}
.metric-agree {
  background: #f1faf4;
  border-color: #b7e3c9 !important;
}
.metric-disagree {
  background: #fdf0ee;
  border-color: #f5c6bd !important;
}
.dispute-bar {
  flex: 1 1 auto;
  height: 8px;
  background: #f3d3cd;
  border-radius: 4px;
  overflow: hidden;
}
.dispute-bar-fill {
  height: 100%;
  background: #e74c3c;
  border-radius: 4px;
}
.att-link {
  cursor: pointer;
}
</style>
